// DepositRequiredOrdersPage.js
import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { checkDuplicatePhoneCount, fetchDepositRequiredOrders, updateOrderStatus, deleteOrder, fetchDepositRequiredSummary } from "../../api/OrderAPIs";
import { fetchDesignById } from "../../api/DesignAPIs";
import { fetchTypeById } from "../../api/TypeAPIs";
import { useAuth } from "../../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../api/URL_Header";
import {
  ChevronRight,
  ChevronLeft,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  FileText,
  Copy,
  Download,
  Link as LinkIcon,
  Edit,
  Trash2,
  Archive,
  Clock,
  CheckCircle,
  AlertTriangle,
  ShoppingCart,
  MessageSquare,
  Truck,
  AlertCircle,
  Layers,
  CopyCheck,
  Filter,
  Save,
  Plus,
  Search,
  Loader,
  XCircle,
  RotateCcw,
  CreditCard,
  Percent,
  Wallet
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  } else if (type === "card") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  } else if (type === "productsCard") {
    return (
      <div className="animate-pulse bg-white/80 backdrop-blur-sm rounded-2xl p-6">
        <div className="h-6 bg-gray-200 rounded w-1/2 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-3/4"></div>
      </div>
    );
  }
  return null;
};

const DepositRequiredOrdersPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [currentReference, setCurrentReference] = useState("Girabatcom.4000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.4000");
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const { data: depositRequiredSummary, isLoading: isLoadingSummary, error: errorSummary } = useQuery({
    queryKey: ["depositRequiredSummary"],
    queryFn: fetchDepositRequiredSummary,
    enabled: authState.role === "Admin",
    staleTime: Infinity,
    placeholderData: { count: 0, totalProfit: 0, totalAmount: 0 },
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  const isLoading = isLoadingSummary;
  const queryError = errorSummary;

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {
        toast.success("تم النسخ بنجاح!");
      }).catch(() => {
        fallbackCopy(text);
      });
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        toast.success("تم النسخ بنجاح!");
      } else {
        toast.error("فشل النسخ، حاول مرة أخرى.");
      }
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
      console.error("Copy failed:", err);
    }

    document.body.removeChild(textArea);
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];

        for (const detail of order.orderDetails) {
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;

          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: type.name,
            driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
            code: design.code || "None",
          };

          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({
              ...baseItem,
              quantityIndex: i + 1,
              totalQuantity: detail.quantity,
            });
          }
        }

        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "New",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchDepositRequiredOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial deposit required orders:", err);
      setError("فشل في تحميل الطلبات التي تتطلب دفعة أولية. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchDepositRequiredOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more deposit required orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const incrementReference = () => {
    const match = currentReference.match(/(\d+)$/);
    if (match) {
      const num = parseInt(match[0], 10);
      const newNum = num + 1;
      const newRef = currentReference.replace(/\d+$/, newNum.toString());
      setCurrentReference(newRef);
      localStorage.setItem("orderReference", newRef);
    }
  };
  
  const handleUpdateStatus = async (orderId, newStatus, order) => {
    let reason = "no reason (normal order)";

    if (newStatus === "Old") {
      try {
        const confirmUpdate = window.confirm("هل تريد تحديث الطلب إلى قديم؟");
        if (!confirmUpdate) return;

        await updateOrderStatus(orderId, newStatus, reason);
        setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
        toast.success("تم تحديث الطلب إلى قديم!");

      } catch (err) {
        console.error(err);
        toast.error("فشل في العملية: " + (err.message || "خطأ غير معروف"));
      }
      return;
    }

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    } else if (newStatus === "Spam") {
      reason = window.prompt("يرجى إدخال سبب :");
      if (reason === null) return;
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب!");
        return;
      }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders(prev => prev.filter(o => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      console.error("Error deleting order:", err);
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  const handleCopyMessage = (order) => {
    const itemsList = order.items.map(item => `النوع: ${item.type} - الكود: ${item.code}`).join('\n');

    const messages = [
      `أهلاً أ/ "${order.name}" 🌸
      مع حضرتك فاطمة من فريق جرابتكم ستور 💛
      بنراجع معاك تفاصيل الطلب قبل ما نبدأ التنفيذ، لأن كل تصميم بيتعمل مخصوص لك 🎨✨

      تفاصيل الطلب:
      ${itemsList}

      بمجرد تأكيدك هنبدأ فورًا في التجهيز والشحن ⚡🚚

      لتأكيد الطلب: رد بـ (نعم - تأكيد) ✅
      لو في تعديل أو إلغاء، قولنا قبل البدء 💬
      شكرًا لثقتك فينا 🤍🌷`,
    ];

    const randomMessage = messages[Math.floor(Math.random() * messages.length)];
    copyToClipboard(randomMessage);
  };

  const handleCopyBostaMessage = () => {
    const message = `
بنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅
تنويه مهم:
في رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  

كمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾
يُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  

شكرًا لاختيارك جرابتكم 🌷`;
    copyToClipboard(message);
  };

  const handleCopyAddressClarification = () => {
    const message = `أهلاً بحضرتك

العنوان المسجل غير واضح أو ناقص تفاصيل مهمة لتسليم الطلب بنجاح

برجاء توضيح العنوان بالتفصيل:
• اسم الشارع
• رقم العمارة / الشقة
• معالم قريبة (مثل: بجوار صيدلية، أمام مدرسة، إلخ)

شكرًا لتفهمك، هنبدأ تجهيز الطلب فور توضيح العنوان`;

    copyToClipboard(message);
  };

  // دالة لتقريب الرقم لأقرب مضاعف 5
  const roundToNearest5 = (num) => {
    return Math.round(num / 5) * 5;
  };

  // دالة نسخ رسالة طلب الدفعة الأولية + فتح الواتساب
  const handleCopyDepositMessage = (order) => {
    const total = order.total || 0;
    const deposit = Math.round((total * 0.25) / 5) * 5; // تقريب 25% لأقرب مضاعف 5

    const message = `مساء الخير يا فندم 🌙  
مع حضرتك فاطمة من جرابتكم ستور 💛  

حابة أبلغ حضرتك إن سياسة الشركة بتقضي إن أي أوردر عدد قطعة فيه 5 قطع أو أكتر، يتطلب دفع دفعة أولية بقيمة 25% من إجمالي الطلب (لضمان جدية الحجز والحفاظ على جودة التنفيذ).

قيمة الدفعة الأولية المطلوبة: *${deposit} جنيه*  

الدفع يكون عن طريق:  
• إنستاباي  
• المحفظة  

على الرقم:  
01559155815  
باسم: حسام  

بمجرد إرسال صورة الإيصال، هنبدأ فوراً في تجهيز طلبك ✨  

في انتظار ردك العزيز، وشكراً لثقتك فينا دايماً 🤍`;

    copyToClipboard(message);
  };

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );

      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) {
          toast.info("تم إلغاء النسخ بسبب تكرار الرقم");
          return;
        }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];

      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const type = item.type || "Unknown Type";
        const driveLink = item.driveLink || "No Link";

        const itemData = `\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
        const itemLine = `${itemNum}${itemData}`;

        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      const finalText = lines.join('\n');
      copyToClipboard(finalText);

      toast.success("تم نسخ الطلب كامل مع التكرار بنجاح!");
    } catch (err) {
      console.error("خطأ في النسخ:", err);
      toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  const updateReferenceFromInput = () => {
    if (referenceInput.trim() && referenceInput.includes(".")) {
      setCurrentReference(referenceInput.trim());
      localStorage.setItem("orderReference", referenceInput.trim());
    }
  };

  // فلترة الطلبات حسب البحث
  const filteredOrders = loadedOrders.filter(order => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  if (queryError) {
    return <div>Error: {queryError.message}</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <CreditCard className="text-orange-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              طلبات تتطلب دفعة أولية
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Stats Cards */}
        {authState.role === "Admin" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <Package className="text-orange-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">ملخص طلبات الدفعة الأولية</h2>
              </div>
              {isLoading ? (
                <SkeletonLoader type="card" />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-orange-50 rounded-xl">
                    <span className="text-gray-600">عدد الطلبات</span>
                    <span className="text-2xl font-bold text-orange-600">{depositRequiredSummary.count}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-xl">
                    <span className="text-gray-600">إجمالي المبلغ</span>
                    <span className="text-2xl font-bold text-green-600">{depositRequiredSummary.totalAmount} ج.م</span>
                  </div>
                </div>
              )}
            </div>

            {/* <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 hover:shadow-2xl transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <Percent className="text-indigo-600" size={24} />
                </div>
                <h2 className="text-xl font-bold text-gray-800">نسبة الدفعة المطلوبة</h2>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-xl">
                  <span className="text-gray-600">النسبة</span>
                  <span className="text-2xl font-bold text-indigo-600">25%</span>
                </div>
              </div>
            </div> */}
          </div>
        )}

        {/* Quick Message Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Address Clarification */}
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-amber-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-amber-100 rounded-xl">
                  <MapPin className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-amber-900 text-lg">طلب توضيح العنوان</h3>
                  <p className="text-sm text-amber-700">إذا كان العنوان غير واضح</p>
                </div>
              </div>
              <button
                onClick={handleCopyAddressClarification}
                className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          {/* Bosta Message */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={handleCopyBostaMessage}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div>

        {/* Reference Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-orange-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                الرقم الحالي
              </label>
              <div className="text-3xl font-bold text-orange-600 bg-orange-50 p-4 rounded-xl text-center border-2 border-orange-200">
                {currentReference}
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={updateReferenceFromInput}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
                placeholder="Girabatcom.100"
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            الرقم المرجعي للنسخ إلى الإكسيل
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 transition-all"
            >
              <Filter size={20} />
              تصفية
            </button>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
                        <th className="p-4 text-right font-bold text-purple-800">رقم الطلب</th>
                        <th className="p-4 text-right font-bold text-purple-800">العميل</th>
                        <th className="p-4 text-right font-bold text-purple-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-purple-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-purple-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-purple-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-purple-800">نسخ</th>
                        <th className="p-4 text-center font-bold text-purple-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-purple-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number */}
                          <td className="p-4">
                            <span className="font-mono font-bold text-purple-600">#{order.orderNumber}</span>
                          </td>

                          {/* Customer Name */}
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span className="font-medium">{order.name}</span>
                            </div>

                          {/* Phone Numbers */}
                          <br />
                            <div className="space-y-1">
                              <a
                                href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition"
                              >
                                <Phone size={14} />
                                {order.phoneNumber}
                              </a>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && (
                                <a
                                  href={`https://wa.me/${order.phoneNumber2.replace(/[^0-9]/g, '').replace(/^0/, '20')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm transition"
                                >
                                  <Phone size={12} />
                                  {order.phoneNumber2}
                                </a>
                              )}
                            </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{fontFamily : "cairo black"}} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                              <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links - Merged */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-teal-700">{item.name}</div>
                                    <div style={{fontFamily:"cairo black"}} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      {item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-green-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                        {!item.code.startsWith("CUSTOM") && (
                                        <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                                          {item.code}
                                        </span>
                                      )}
                                      
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-teal-600 hover:text-teal-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          
                                          {item.code.startsWith("CUSTOM") && (
                                          <button
                                            onClick={() =>
                                              handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)
                                            }
                                            className="flex items-center gap-2 text-white hover:text-white transition-transform hover:scale-110 px-3 py-2"
                                            title="تحميل الصورة"
                                            style={{
                                              backgroundColor: "#15d225",
                                              borderRadius: "4px",
                                            }}
                                          >
                                            <Download size={16} />
                                            <span className="text-sm">Download</span>
                                          </button>
                                        )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {index < order.items.length - 1 && (
                                  <hr className="my-2 border-gray-200" />
                                )}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-4 max-w-[150px]">
                            <p style={{fontFamily:"cairo black"}} className="text-[15px]">{order.note || "—"}</p>
                          </td>     

                          {/* Copy Excel Button */}
                          <td className="p-4">
                            <button
                              onClick={() => handleCopyDepositMessage(order)}
                              className="w-full text-xl bg-amber-500 text-white text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                            >
                              <Wallet size={16} />
                              طلب دفعة 25%
                            </button>
                            <button
                              onClick={() => handleCopyAllExcelData(order)}
                              className="w-full text-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                            >
                              <Copy size={16} />
                              نسخ
                            </button>
                             <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="w-full text-xl bg-purple-500 text-white text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2 mb-3"
                              >
                                <RotateCcw size={16} />
                                تغييرات
                              </button>
                          </td>

                          {/* Actions */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Old", order)}
                                className="bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Archive size={14} />
                                  قديم
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                             
                              
                              <button
                                onClick={() => handleCopyMessage(order)}
                                className="bg-gray-500 text-white px-3 py-2 rounded-lg hover:bg-gray-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <MessageSquare size={14} />
                                  رسالة
                                </div>
                              </button>
                            </div>
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More Button */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <CreditCard className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات تتطلب دفعة أولية</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات التي تحتاج دفعة أولية هنا</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Floating Action Button for Quick Reference Update */}
        <div className="fixed bottom-6 left-6">
          <button
            onClick={incrementReference}
            className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="زيادة الرقم المرجعي"
          >
            <Plus size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepositRequiredOrdersPage;